import { I18n } from '@calendly/i18n'
import { Segment } from '@calendly/segment'
import { Button } from '@calendly/ui/components/button'
import { ContextualBanner } from '@calendly/ui/components/contextual-banner'
import { IconAlertTriangleFilled } from '@calendly/ui/components/glyphs/IconAlertTriangleFilled'
import { IconChevronRight } from '@calendly/ui/components/glyphs/IconChevronRight'
import { IconButton } from '@calendly/ui/components/icon-button'
import { Loader } from '@calendly/ui/components/loader'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { forwardRef } from 'react'
import { Link } from 'react-router-dom'

import { useConnectOauthMutation } from 'features/calendar_connections'

import { CALENDAR_CONNECTIONS_POPUP_ROUTE } from '../constants'
import { Actions, Card, CardButton, Content } from '../integration_card'
import { getCalendarSettingsReturnTo, isCalendarSettingsModalOpen } from '../oauth_return_path'
import { headerId, ProviderHeader, ProviderLogo } from './header'
import { useIntegrationProviderError } from './use_integration_provider_error'

const ConnectionErrorIcon = forwardRef((props, ref) => (
  <UiIcon ref={ref} icon={IconAlertTriangleFilled} size="small" {...props} />
))

const ConnectionError = ({ provider }) => {
  const { queryError } = useIntegrationProviderError({ provider })

  if (!queryError) return null

  return (
    <Content>
      <ContextualBanner variant="danger" icon={ConnectionErrorIcon}>
        {queryError}
      </ContextualBanner>
    </Content>
  )
}

const OauthAccountProvider = ({ provider, minimalistic }) => {
  const [connectOauth, { isLoading }] = useConnectOauthMutation()

  const connectOauthHandler = () => {
    Segment.track('Calendar Connected', { calendar_type: provider })
    connectOauth({
      provider,
      ...(isCalendarSettingsModalOpen() && { returnTo: getCalendarSettingsReturnTo() }),
    })
  }

  if (minimalistic) {
    return (
      <Card hideLogo>
        <CardButton
          onClick={connectOauthHandler}
          aria-describedby={headerId(provider)}
          title={`${I18n.t('publisher.action.connect')} ${I18n.t(`publisher.calendar.${provider}.title`)}`}
        />
        <ProviderHeader provider={provider} id={headerId(provider)} />
        <ConnectionError provider={provider} />
        <Actions>
          <IconButton
            size="small"
            title={I18n.t('publisher.action.connect')}
            onClick={connectOauthHandler}
          >
            {isLoading ? <Loader size="small" /> : <UiIcon icon={IconChevronRight} size="small" />}
          </IconButton>
        </Actions>
      </Card>
    )
  }

  return (
    <Card>
      <ProviderLogo provider={provider} />
      <ProviderHeader provider={provider} id={headerId(provider)} />
      <ConnectionError provider={provider} />
      <Actions>
        <Button
          size="small"
          loading={isLoading}
          onClick={connectOauthHandler}
          aria-describedby={headerId(provider)}
        >
          {I18n.t('publisher.action.connect')}
        </Button>
      </Actions>
    </Card>
  )
}

const ExchangeAccountProvider = ({ minimalistic }) => {
  if (minimalistic) {
    return (
      <Card hideLogo>
        <CardButton
          as={Link}
          aria-describedby={headerId('exchange')}
          title={`${I18n.t('publisher.action.connect')} ${I18n.t('publisher.calendar.exchange.title')}`}
          to={(location) => ({
            ...location,
            pathname: CALENDAR_CONNECTIONS_POPUP_ROUTE.newExchangePath,
            state: { withGoBack: true },
          })}
        />
        <ProviderHeader provider="exchange" id={headerId('exchange')} />
        <ConnectionError provider="exchange" />
        <Actions>
          <IconButton
            as={Link}
            size="small"
            title={I18n.t('publisher.action.connect')}
            to={(location) => ({
              ...location,
              pathname: CALENDAR_CONNECTIONS_POPUP_ROUTE.newExchangePath,
              state: { withGoBack: true },
            })}
          >
            <UiIcon icon={IconChevronRight} size="small" />
          </IconButton>
        </Actions>
      </Card>
    )
  }
  return (
    <Card>
      <ProviderLogo provider="exchange" />
      <ProviderHeader provider="exchange" id={headerId('exchange')} />
      <ConnectionError provider="exchange" />
      <Actions>
        <Button
          as={Link}
          size="small"
          to={CALENDAR_CONNECTIONS_POPUP_ROUTE.newExchangePath}
          aria-describedby={headerId('exchange')}
        >
          {I18n.t('publisher.action.connect')}
        </Button>
      </Actions>
    </Card>
  )
}

export const ConnectCalendarAccount = ({ provider, minimalistic = false }) => {
  switch (provider) {
    case 'google':
    case 'outlook':
      return <OauthAccountProvider provider={provider} minimalistic={minimalistic} />
    case 'exchange':
      return <ExchangeAccountProvider minimalistic={minimalistic} />
    default:
      return null
  }
}
